import {hpcAttrs} from '@github-ui/hpc'
import {testIdProps} from '@github-ui/test-id-props'
import {BaseStyles} from '@primer/react'
import {clsx} from 'clsx'
import {memo, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState} from 'react'

import {useSliceBy} from '../../features/slicing/hooks/use-slice-by'
import {useSubIssuesLoading} from '../../features/sub-issues/use-sub-issues-loading'
import {useTableHierarchy} from '../../features/sub-issues/use-table-hierarchy'
import {resetScrollPositionImmediately} from '../../helpers/scroll-utilities'
import {addNoGroupWhenGroupsDisabled} from '../../helpers/table-group-utilities'
import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import {getIsTableGrouped} from '../../hooks/tanstack-table/helpers/table-api'
import type {Table as TableType} from '../../hooks/tanstack-table/helpers/types'
import {useColumnWidthVariables} from '../../hooks/tanstack-table/hooks/use-column-width-variables'
import {AddColumnHeaderColumnDef} from '../../hooks/tanstack-table/providers/columns/add-column-header-column-def'
import {TableProvider, useTableInstance} from '../../hooks/tanstack-table/providers/table-provider'
import {ReactTable} from '../../hooks/tanstack-table/react-table'
import {useEnabledFeatures} from '../../hooks/use-enabled-features'
import {useInitialTableFocusAction} from '../../hooks/use-initial-table-focus-action'
import {useMeasureScrollbars} from '../../hooks/use-measure-scrollbars'
import {useTableRowHeight} from '../../hooks/use-table-row-height'
import {LoadingStates, useViewLoadingState} from '../../hooks/use-view-loading-state'
import {useViews} from '../../hooks/use-views'
import {useVisibleFields} from '../../hooks/use-visible-fields'
import {useBulkAddNotifications} from '../../state-providers/bulk-add-notifications/bulk-add-notifications-state-provider'
import {usePaginatedMemexItemsQuery} from '../../state-providers/memex-items/queries/use-paginated-memex-items-query'
import {BulkAddNotifications} from '../bulk-add-notifications'
import {MoveDialog, MoveDialogContext, type MoveDialogProps} from '../move-item/move-dialog/move-dialog'
import {OmnibarContainer} from '../omnibar/omnibar-container'
import {PROJECT_ITEMS_REGION_ID} from '../slicer-panel/constants'
import {SlicerItemsProvider, useSlicerItems} from '../slicer-panel/slicer-items-provider'
import {SlicerPanel} from '../slicer-panel/slicer-panel'
import {TableDragToFillProvider} from './bulk-fill-values'
import {TABLE_HEADER_HEIGHT} from './constants'
import {CopilotAssignmentModalProvider} from './copilot-assignment-modal-provider'
import {CopilotAssignmentModalRenderer} from './copilot-assignment-modal-renderer'
import {CopyPasteProvider} from './hooks/use-copy-paste'
import {useIsOmnibarFixed} from './hooks/use-is-omnibar-fixed'
import {useScrollToNewItem} from './hooks/use-scroll-to-new-item'
import {useTableBulkActionsAutoscroll} from './hooks/use-table-bulk-actions-autoscroll'
import {focusSearchInput, type TableFocusMeta, TableNavigationProvider, useTableNavigation} from './navigation'
import {useRecordComponent} from './performance-measurements'
import styles from './table.module.css'
import {TableBodyWithObserver} from './table-body-with-observer'
import {TableCellBulkSelectionProvider} from './table-cell-bulk-selection'
import type {TableDataType} from './table-data-type'
import {TableFilterInput} from './table-filter-input'
import {TableOmnibar} from './table-omnibar'
import {PLACEHOLDER_ROW_COUNT} from './table-pagination'
import {useItemData} from './use-item-data'
import {useSelectionBlur} from './use-selection-blur'
import {useTableFocus} from './use-table-focus'

interface TableRef {
  focusIn: () => void
}

const getHeaderFocus = () => focusSearchInput().focus

export const Table = memo(function TableComponent({ref: forwardedRef, ..._}: {ref?: React.Ref<TableRef>}) {
  const {visibleFields} = useVisibleFields()
  const tableProps = useMemo(
    () => ({fields: visibleFields, pinnedRightColumnDefs: [AddColumnHeaderColumnDef]}),
    [visibleFields],
  )
  return (
    <ReactTable {...tableProps}>
      {table => (
        <TableProvider table={table}>
          <SlicerItemsProvider>
            <TableLayout ref={forwardedRef} />
          </SlicerItemsProvider>
        </TableProvider>
      )}
    </ReactTable>
  )
})

function TableLayout({ref: forwardedRef, ..._}: {ref?: React.Ref<TableRef>}) {
  useRecordComponent('Table', 'Table', '')
  const {sliceField} = useSliceBy()
  const {slicerItems, totalSlicerItemCount} = useSlicerItems()
  const tableInstance = useTableInstance()

  const metaRef = useRef<TableFocusMeta>({tableInstance, getHeaderFocus})

  return (
    <CopilotAssignmentModalProvider>
      <TableNavigationProvider metaRef={metaRef}>
        <TableCellBulkSelectionProvider>
          <TableDragToFillProvider>
            <CopyPasteProvider>
              {sliceField && <SlicerPanel slicerItems={slicerItems} totalSlicerItemCount={totalSlicerItemCount} />}
              <TableFilterInput />
              <BulkAddNotifications />
              <TableInner table={tableInstance} ref={forwardedRef} />
              <CopilotAssignmentModalRenderer />
            </CopyPasteProvider>
          </TableDragToFillProvider>
        </TableCellBulkSelectionProvider>
      </TableNavigationProvider>
    </CopilotAssignmentModalProvider>
  )
}

type InnerTableProps = {
  table: TableType<TableDataType>
}

function TableInner({ref: forwardedRef, table}: InnerTableProps & {ref?: React.Ref<TableRef | null>}) {
  const {onKeyDown, onBlur} = useTableFocus()
  const {navigationDispatch} = useTableNavigation()
  const [moveDialogProps, setMoveDialogProps] = useState<MoveDialogProps | null>(null)
  const {shouldShowSkeleton} = useBulkAddNotifications()
  const {memex_disable_autofocus, memex_small_viewport_a11y} = useEnabledFeatures()

  const initialFocusAction = useInitialTableFocusAction(table)
  const focusIn = useCallback(() => {
    if (initialFocusAction) navigationDispatch(initialFocusAction)
  }, [navigationDispatch, initialFocusAction])

  const topLevelRows = table.getPreExpandedRowModel().rows
  const expandedRows = table.getExpandedRowModel().rows
  const tableIsGrouped = getIsTableGrouped(table)
  const allColumns = table.getAllColumns()
  const columnWidthVariables = useColumnWidthVariables(allColumns)

  // focus in on init
  useEffect(() => {
    if (!memex_disable_autofocus) focusIn()
  }, [focusIn, memex_disable_autofocus])

  useImperativeHandle(forwardedRef, () => ({focusIn}))

  const scrollRef = useRef<HTMLDivElement | null>(null)
  const {hasWritePermissions} = ViewerPrivileges()
  const {loadingState} = useViewLoadingState()
  const {hasNextPage} = usePaginatedMemexItemsQuery()
  const itemData = useItemData(table)

  const {hierarchyEnabled} = useTableHierarchy()
  const {rowHeightAlignItems, rowHeightPx, rowHeightPaddingY} = useTableRowHeight()
  const {getCountLoadingSubIssues} = useSubIssuesLoading()
  const subIssuePlaceholderRowCount = hierarchyEnabled ? getCountLoadingSubIssues() : 0

  const placeholderRowCount = hasNextPage || shouldShowSkeleton ? PLACEHOLDER_ROW_COUNT : 0
  const additionalRows = placeholderRowCount + subIssuePlaceholderRowCount

  const effectiveTableHeight =
    loadingState === LoadingStates.loaded
      ? (expandedRows.length + additionalRows) * rowHeightPx + TABLE_HEADER_HEIGHT
      : rowHeightPx + TABLE_HEADER_HEIGHT
  const isOmnibarFixed = useIsOmnibarFixed({
    isGrouped: tableIsGrouped,
    totalHeight: effectiveTableHeight,
    scrollRef,
  })
  const {horizontalScrollbarSize, verticalScrollbarSize} = useMeasureScrollbars(scrollRef)

  useTableBulkActionsAutoscroll({
    scrollRef,
    isGrouped: tableIsGrouped,
    horizontalScrollbarSize,
  })

  // When a new item is added, wait for paint and scroll the table to the new
  // row (the bottom).
  const {onNewItem} = useScrollToNewItem(scrollRef, expandedRows, {left: 0, rowHeight: rowHeightPx})

  const {currentView} = useViews()
  const currentViewNumber = currentView?.number

  const {containerRef} = useSelectionBlur()

  useLayoutEffect(() => {
    resetScrollPositionImmediately(scrollRef.current)
  }, [currentViewNumber])

  const tableGroups = tableIsGrouped ? addNoGroupWhenGroupsDisabled(topLevelRows) : undefined

  return (
    <>
      <MoveDialogContext value={useMemo(() => ({setMoveDialogProps}), [setMoveDialogProps])}>
        {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus */}
        <div
          id={PROJECT_ITEMS_REGION_ID}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          role="grid"
          {...testIdProps('table-root')}
          style={{
            ...columnWidthVariables,
            '--table-cell-align-items': rowHeightAlignItems,
            '--table-cell-height': `${rowHeightPx}px`,
            '--table-cell-padding-y': `${rowHeightPaddingY}px`,
          }}
          className={clsx(
            styles.tableRoot,
            !tableGroups && styles.gridLines,
            !tableGroups && isOmnibarFixed && styles.fixedOmnibar,
          )}
        >
          {moveDialogProps && (
            <MoveDialog
              close={() => {
                setMoveDialogProps(null)
              }}
              {...moveDialogProps}
            />
          )}
          <div
            data-memex-small-viewport-a11y={memex_small_viewport_a11y}
            className={styles.tableBodyContainer}
            {...hpcAttrs()}
          >
            <BaseStyles style={{fontSize: '14px'}}>
              <TableBodyWithObserver
                scrollRef={scrollRef}
                containerRef={containerRef}
                table={table}
                isOmnibarFixed={isOmnibarFixed}
                loadingState={loadingState}
                hasWritePermissions={hasWritePermissions}
                itemData={itemData}
              />
            </BaseStyles>
          </div>
          {(!tableGroups || tableGroups.length === 0) && hasWritePermissions && (
            <OmnibarContainer
              horizontalScrollbarSize={horizontalScrollbarSize}
              verticalScrollbarSize={verticalScrollbarSize}
              isFixed={isOmnibarFixed}
              effectiveTableHeight={effectiveTableHeight}
            >
              <TableOmnibar onAddItem={onNewItem} isFixed={isOmnibarFixed} />
            </OmnibarContainer>
          )}
        </div>
      </MoveDialogContext>
    </>
  )
}
